<script setup lang="ts">
    import { RouterLink, useRoute } from 'vue-router';
    import { GripHorizontalIcon, StarIcon } from '@lucide/vue';
    import { DashboardListItem } from '@/js/services/api/dashboard/types';
    import { __ } from '@/js/stores/expressions';
    import IconButton from '../IconButton.vue';

    const props = defineProps<{
        dashboard: DashboardListItem;
        active?: boolean;
        sortable?: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'select', id: string): void;
        (e: 'favoriteToggle', id: string): void;
    }>();

    const route = useRoute();

    const onFavoriteClick = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        emit('favoriteToggle', props.dashboard.id);
    };
</script>

<template>
    <div
        class="flex items-center gap-2 py-1.5 select-none"
        :class="{ 'font-semibold': props.active }"
    >
        <span
            v-if="sortable"
            class="drag-handle text-gray-medium cursor-grab active:cursor-grabbing shrink-0"
            aria-hidden="true"
        >
            <GripHorizontalIcon class="size-5" />
        </span>
        <RouterLink
            :to="{ query: { ...route.query, id: dashboard.id } }"
            class="flex-1 min-w-0 truncate text-gray-dark text-body rounded-sm custom-focus hover:underline"
            :aria-current="props.active ? 'page' : undefined"
            draggable="false"
            @click="emit('select', dashboard.id)"
        >
            {{ dashboard.title }}
        </RouterLink>
        <IconButton
            size="size-5"
            area-size="size-6"
            :tooltip="dashboard.isFavorite ? __('unfavorite-dashboard') : __('favorite-dashboard')"
            :aria-pressed="dashboard.isFavorite"
            @click="onFavoriteClick"
        >
            <StarIcon
                :class="{
                    'text-yellow fill-current': dashboard.isFavorite,
                    'text-gray-light hover:text-gray-medium focus-visible:text-gray-medium':
                        !dashboard.isFavorite,
                }"
            />
        </IconButton>
    </div>
</template>
